import React from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';

// leaflet
import L from 'leaflet';
import {
  Marker, Popup, Tooltip,
} from 'react-leaflet';

import {
  Dam1Icon,
  Dam2Icon,
  Dam3Icon,
  Dam4Icon,
  Dam5Icon,
}
  from '../../../assets/svg/Icon';

// components
import StyledMarker from '../../../components/marker/Marker';
import DamLargeColor from '../../../components/color/dam/DamLargeColor';

// leaflet
import 'leaflet/dist/leaflet.css';
import '../../../components/map/Style.css';

import { styles } from '../style';

const useStyles = makeStyles(styles);

function styleMarker(color) {
  return ({
    type: 'dam',
    colorinfo: {
      icon: 'Dam5Icon',
      label: 'shapeSquare',
      color,
    },
  });
}

export default function DamMap(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { datas } = props;

  const setHighlightIcon = (val) => {
    const status = DamLargeColor(val);
    let icon = '';

    // setup icon
    if (val > 80) {
      icon = <Dam5Icon style={{ fontSize: '6rem', fill: status.color.statusColor }} />;
    } else if (val > 60) {
      icon = <Dam4Icon style={{ fontSize: '6rem', fill: status.color.statusColor }} />;
    } else if (val > 40) {
      icon = <Dam3Icon style={{ fontSize: '6rem', fill: status.color.statusColor }} />;
    } else if (val > 20) {
      icon = <Dam2Icon style={{ fontSize: '6rem', fill: status.color.statusColor }} />;
    } else if (val > 0) {
      icon = <Dam1Icon style={{ fontSize: '6rem', fill: status.color.statusColor }} />;
    } else {
      icon = <Dam1Icon style={{ fontSize: '6rem', fill: status.color.statusColor }} />;
    }

    return icon;
  };

  const eachLargeDamData = (data, i) => {
    if (data.dam.dam_lat && data.dam.dam_long) {
      // marker position
      const position = [parseFloat(data.dam.dam_lat), parseFloat(data.dam.dam_long)];

      // marker icons
      let markerBgColor = '#FFF';
      if (data.dam_uses_water_percent <= 30) {
        markerBgColor = '#FFC100';
      } else if (data.dam_storage_percent > 80) {
        markerBgColor = '#C70200';
      }

      const styleClass = styleMarker(markerBgColor);
      const icon = L.divIcon({
        className: 'styleClass',
        html: ReactDOMServer.renderToString(
          <StyledMarker category={styleClass.type} type={styleClass.colorinfo} />,
        ),
      });

      // highlight icon in popup
      const highlightIcon = setHighlightIcon(parseFloat(data.dam_storage_percent).toFixed(0));
      const color = DamLargeColor(parseFloat(data.dam_storage_percent).toFixed(0));

      return (
        <Marker
          id={data.id}
          key={i}
          position={position}
          icon={icon}
        >
          <Popup autoPan>
            <div>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={2}>
                      <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                        {`${t('อ่างเก็บน้ำ')}${data.dam.dam_name.th} `}
                      </Box>
                      <Box fontFamily="Kanit" fontSize="12px">
                        {`${t('จ.')}${data.geocode.province_name.th}`}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={2}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Box>{highlightIcon}</Box>
                        <Box className={classes.statusValue}>
                          <span style={{ color: color.color.statusColor }}>
                            {`${parseFloat(data.dam_storage_percent).toFixed(0)}%`}
                          </span>
                        </Box>
                      </Box>
                      <Box mb={2} fontSize="16px" fontFamily="Kanit">
                        {t('ปริมาณน้ำกักเก็บปัจจุบัน (% รนก.)')}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding="none" align="center" colSpan={3} />
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={classes.hiddenLine}
                    >
                      <Box fontWeight={600} fontSize={12}>{t('ปริมาณน้ำกักเก็บ')}</Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">
                      {data.dam_storage}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={classes.hiddenLine}
                    >
                      <Box fontWeight={600} fontSize={12}>
                        {t('น้ำไหลลง')}
                        {' '}
                        /
                        {' '}
                        {t('ระบาย')}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.hiddenLine} align="right">
                      {data.dam_inflow}
                      {' '}
                      /
                      {' '}
                      {data.dam_released}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={classes.hiddenLine}
                      colSpan={2}
                    >
                      <Box fontWeight={400} fontSize={12} colSpan={3}>{t('หน่วย')} : ({t('ล้าน ลบ.ม.')})</Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.hiddenLine} colSpan={2}>
                      <Box fontSize="10px">
                        {`${t('ข้อมูล')} : ${data.agency.agency_name.th}`}
                      </Box>
                      <Box fontSize="10px">
                        {`${t('ล่าสุด')} : ${data.dam_date}`}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Popup>
          <Tooltip>
            <span>
              {data.dam_uses_water_percent <= 30 && (
                <strong>{t('น้ำน้อยวิกฤต')}</strong>
              )}
              {data.dam_storage_percent > 80 && (
                <strong>{t('น้ำมากวิกฤต')}</strong>
              )}
              <hr />
              <strong>
                {t('อ่างเก็บน้ำ')}
                {data.dam.dam_name.th}
              </strong>
              <br />
              {`${t('ต.')}${data.geocode.tumbon_name.th} ${t('อ.')}${data.geocode.amphoe_name.th} `}
              <br />
              {`${t('จ.')} ${data.geocode.province_name.th}`}
            </span>
          </Tooltip>
        </Marker>
      );
    }

    return false;
  };

  return (
    <>
      {Object.prototype.hasOwnProperty.call(datas, 'red') && (
        datas.red.map(eachLargeDamData)
      )}
      {Object.prototype.hasOwnProperty.call(datas, 'yellow') && (
        datas.yellow.map(eachLargeDamData)
      )}
    </>
  );
}

DamMap.propTypes = {
  datas: PropTypes.instanceOf(Object).isRequired,
};
